import { useContext, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { Group, Role, User } from '@esg/esg-global-types';

import { UserContext } from '../../context/UserContext';
import StatusChip from './StatusChip';
import EditUserProfileTab from './EditUserProfileTab';
import EditUserAccessTab from './EditUserAccessTab';
import EditUserPermissionsTab from './EditUserPermissionsTab';
import { useUnsavedChangesGuard } from './hooks/useUnsavedChangesGuard';
import { useUserMutations } from './hooks/useUserMutations';
import {
  EMAIL_INVALID_MESSAGE,
  EMAIL_TAKEN_MESSAGE,
  isEmailTaken,
  validEmailRe
} from './shared/email_validation';

interface EditUserDrawerProps {
  user: User;
  groups: Group[];
  roles: Role[];
  onSaved: (updated: User) => void;
  onClose: () => void;
}

type TabKey = 'profile' | 'access' | 'permissions';

const TAB_DEFS: Array<{ key: TabKey; label: string }> = [
  { key: 'profile', label: 'Profile' },
  { key: 'access', label: 'Access' },
  { key: 'permissions', label: 'Permissions' }
];

const dirtyKeys = (prev: User, next: User): Array<keyof User> =>
  (Object.keys(next) as Array<keyof User>).filter(
    (k) => JSON.stringify(prev[k]) !== JSON.stringify(next[k])
  );

/** Tabbed Edit User drawer (Profile / Access / Permissions) with per-tab save. */
const EditUserDrawer = ({ user, groups, roles, onSaved, onClose }: EditUserDrawerProps) => {
  const { user: currentUser } = useContext(UserContext);
  const [activeTab, setActiveTab] = useState<TabKey>('profile');
  const [baseline, setBaseline] = useState<User>(user);
  const [draft, setDraft] = useState<User>(user);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailChecking, setEmailChecking] = useState<boolean>(false);
  const [confirmEmailChange, setConfirmEmailChange] = useState<boolean>(false);
  const [offerReinvite, setOfferReinvite] = useState<boolean>(false);
  const mutations = useUserMutations();

  const dirty = useMemo(() => dirtyKeys(baseline, draft).length > 0, [baseline, draft]);

  const profileKeys: ReadonlyArray<keyof User> = [
    'first_name',
    'last_name',
    'phone',
    'type',
    'email'
  ];
  const accessKeys: ReadonlyArray<keyof User> = ['super_admin'];

  const tabDirty = (tab: TabKey): boolean => {
    const changed = dirtyKeys(baseline, draft);
    if (tab === 'profile') return changed.some((k) => profileKeys.includes(k));
    if (tab === 'access') {
      return changed.some((k) => accessKeys.includes(k) || k === 'groups');
    }
    if (tab === 'permissions') return changed.includes('groups');
    return false;
  };

  const guard = useUnsavedChangesGuard({ dirty, onDiscard: onClose });

  const patch = (p: Partial<User>) => setDraft((cur) => ({ ...cur, ...p }));

  const tabKeys = (tab: TabKey): ReadonlyArray<keyof User> => {
    if (tab === 'profile') return profileKeys;
    if (tab === 'access') return [...accessKeys, 'groups'];
    return ['groups'];
  };

  // A case-only edit is not a change of identity — the backend normalises to
  // lowercase — so it neither needs confirming nor re-checking for uniqueness.
  const emailChanged: boolean =
    draft.email.trim().toLowerCase() !== baseline.email.trim().toLowerCase();

  /**
   * Validates the entered address for shape then availability, leaving the
   * message on `emailError`. Purely a pre-submit guard: the backend updateUser
   * re-checks both and stays authoritative.
   */
  const validateEmail = async (): Promise<boolean> => {
    if (!emailChanged) {
      setEmailError(null);
      return true;
    }
    if (!validEmailRe.test(draft.email.trim())) {
      setEmailError(EMAIL_INVALID_MESSAGE);
      return false;
    }
    setEmailChecking(true);
    try {
      const taken: boolean = await isEmailTaken(draft.email);
      setEmailError(taken ? EMAIL_TAKEN_MESSAGE : null);
      return !taken;
    } finally {
      setEmailChecking(false);
    }
  };

  /**
   * Whether this user is sitting on a password set-up link that an email change
   * would orphan — the backend revokes it, since it is keyed by the old address.
   *
   * `live` is the only state that means "a valid set-up link exists right now",
   * because only password users ever mint one: the welcome-email service builds
   * a link (and its `jti_claims` token) solely when the address is not on an SSO
   * domain. Gating on it therefore excludes, by construction rather than by
   * accident, everyone for whom re-sending would be meaningless:
   *
   * - Created with "don't send an invitation" — no token was ever minted, so it
   *   reads `used` for the invite window and `expired` after.
   * - SSO users — no link exists at any point in their lifecycle, and with no
   *   `invitation_sent_at` to date it from they read `expired` from creation.
   *   They sign in through their IdP, so an email change orphans nothing.
   * - Tokens already consumed or genuinely lapsed.
   *
   * Do NOT widen this to include `expired` — that state conflates a lapsed
   * invite with both cases above, and would prompt admins to re-send a link
   * that cannot exist. The list view keeps explicit Resend / Reissue actions
   * for the cases this deliberately stays quiet about.
   */
  const hasLiveInvite: boolean = user.invitation_token_state === 'live';

  const commitCurrentTab = async () => {
    const keys = tabKeys(activeTab);
    const changed = dirtyKeys(baseline, draft).filter((k) => keys.includes(k));
    const tabPatch = Object.fromEntries(changed.map((k) => [k, draft[k]])) as Partial<User>;
    // `changed` comes from the strict JSON dirty check, so a case-only edit
    // counts as a change here while the backend treats it as a no-op (it drops
    // the field, burns no token). Without `emailChanged` — the case-insensitive
    // comparison — editing ada@test.com to ADA@test.com would offer the
    // re-invite, and accepting it mints a new jti_claims token over the live
    // one: the link already in the user's inbox stops working and a second
    // email goes out, for an edit that changed nothing.
    const emailWasChanged: boolean = changed.includes('email') && emailChanged;
    const result = await mutations.updateUser({ ...tabPatch, id: user.id });
    if (result) {
      // Take each saved field from the server's response rather than echoing
      // the draft back. The API normalises the address (trim + lowercase), so
      // saving `  New@Corp.com  ` and then showing `draft.email` would leave
      // the drawer and the row displaying a spelling Firestore does not hold.
      // `return_data: true` is already set, so the response is authoritative;
      // the draft stays the fallback for any field it does not echo.
      const saved = { ...baseline };
      changed.forEach((k) => {
        const returned = (result as Partial<User>)[k];
        (saved as Record<keyof User, unknown>)[k] = returned === undefined ? draft[k] : returned;
      });
      onSaved({ ...saved });
      setBaseline(saved);
      // Re-seed the draft too, or the normalised value would read as an
      // unsaved change and re-arm the unsaved-changes guard. Only the keys this
      // tab just saved — spreading all of `saved` would revert another tab's
      // still-unsaved edits to baseline.
      setDraft((cur) => ({
        ...cur,
        ...(Object.fromEntries(changed.map((k) => [k, saved[k]])) as Partial<User>)
      }));
      // Offer the re-invite only once the save has actually landed — the old
      // link is revoked as part of that commit, so before this point there is
      // nothing to replace.
      if (emailWasChanged && hasLiveInvite) setOfferReinvite(true);
    }
  };

  const resendInviteToNewAddress = async () => {
    setOfferReinvite(false);
    await mutations.resendInvite({ name: draft.first_name, email: draft.email });
  };

  const saveCurrentTab = async () => {
    // An email change is the one profile edit that alters how someone signs in,
    // so it is re-validated and confirmed before it is sent.
    if (activeTab === 'profile' && emailChanged) {
      if (!(await validateEmail())) return;
      setConfirmEmailChange(true);
      return;
    }
    await commitCurrentTab();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, pr: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">
            {draft.first_name} {draft.last_name}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {draft.email}
            </Typography>
            <StatusChip user={draft} />
          </Stack>
        </Box>
        <IconButton onClick={() => guard.requestClose()} aria-label="Close drawer">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />

      <Tabs
        value={activeTab}
        onChange={(_, v: TabKey) => setActiveTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        {TAB_DEFS.map((t) => (
          <Tab
            key={t.key}
            value={t.key}
            label={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                {t.label}
                {tabDirty(t.key) && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: 'warning.main'
                    }}
                  />
                )}
              </Box>
            }
          />
        ))}
      </Tabs>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {activeTab === 'profile' && (
          <EditUserProfileTab
            draft={draft}
            onChange={(p) => {
              // Clear a stale message while the address is being retyped.
              if (p.email !== undefined && emailError) setEmailError(null);
              patch(p);
            }}
            emailError={emailError}
            emailChecking={emailChecking}
            onEmailBlur={() => void validateEmail()}
            canEditEmail={!!currentUser?.super_admin}
          />
        )}
        {activeTab === 'access' && (
          <EditUserAccessTab draft={draft} groups={groups} roles={roles} onChange={patch} />
        )}
        {activeTab === 'permissions' && (
          <EditUserPermissionsTab draft={draft} groups={groups} roles={roles} onChange={patch} />
        )}
      </Box>

      <Divider />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          p: 2,
          bgcolor: 'background.paper'
        }}
      >
        <Button onClick={() => guard.requestClose()}>Cancel</Button>
        <Button
          variant="contained"
          onClick={saveCurrentTab}
          disabled={
            !tabDirty(activeTab) || emailChecking || (activeTab === 'profile' && !!emailError)
          }
        >
          Save {activeTab}
        </Button>
      </Box>

      <Dialog open={guard.isModalOpen} onClose={guard.cancelDiscard}>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You have unsaved changes across one or more tabs. Closing this drawer will lose them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={guard.cancelDiscard}>Keep editing</Button>
          <Button color="error" onClick={guard.confirmDiscard}>
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmEmailChange} onClose={() => setConfirmEmailChange(false)}>
        <DialogTitle>Change sign-in email?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            {baseline.email} will become {draft.email.trim().toLowerCase()}.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            They will sign in with the new address immediately and will be signed out of any active
            session. Their authenticator app keeps working, but will still display the old address
            until they re-enrol.
          </Typography>
          {hasLiveInvite && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Their outstanding set-up link is tied to the old address and will stop working. You
              can send a fresh one straight after saving.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmEmailChange(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              setConfirmEmailChange(false);
              await commitCurrentTab();
            }}
          >
            Change email
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={offerReinvite} onClose={() => setOfferReinvite(false)}>
        <DialogTitle>Send a new set-up link?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {draft.first_name} had not finished setting up their account, and the link they were
            sent no longer works now that their address has changed.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Sending a fresh link to {draft.email} lets them finish. You can also do this later from
            the users list.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOfferReinvite(false)}>Not now</Button>
          <Button variant="contained" onClick={resendInviteToNewAddress}>
            Send link
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditUserDrawer;
